// apps/hyperseguros-web/src/components/Nav/index.tsx

'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, useMediaQuery, useTheme } from '@mui/material'
import NavBar from './NavBar'
import DesktopMegaMenu from './DesktopMegaMenu'
import MobileMenu from './MobileMenu'
import { useNavState } from './useNavState'
import type { TNavConfig, TNavMenu } from './types'

export type {
  TNavConfig,
  TNavItem,
  TNavLabels,
  TNavLink,
  TNavMenu,
  TNavCTA,
  TNavMenuGroup,
} from './types'

const BAR_HEIGHT_DESKTOP = 64
const BAR_HEIGHT_MOBILE = 56
const HOVER_CLOSE_DELAY_MS = 150

type NavProps = {
  config: TNavConfig
}

export default function Nav({ config }: NavProps) {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'), {
    defaultMatches: true,
  })
  const barHeight = isDesktop ? BAR_HEIGHT_DESKTOP : BAR_HEIGHT_MOBILE

  const state = useNavState()
  const viewportHeight = useVisualViewportHeight()
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const navRef = useRef<globalThis.HTMLElement | null>(null)

  const cancelCloseTimer = useCallback(() => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])

  useEffect(() => {
    return () => cancelCloseTimer()
  }, [cancelCloseTimer])

  const scheduleClose = useCallback(() => {
    cancelCloseTimer()
    closeTimer.current = setTimeout(() => {
      state.closeMenu()
      closeTimer.current = null
    }, HOVER_CLOSE_DELAY_MS)
  }, [cancelCloseTimer, state.closeMenu])

  const currentMenu = useMemo<TNavMenu | null>(() => {
    if (!state.openId) return null
    const match = config.items.find(
      (i) => i.type === 'menu' && i.label === state.openId
    )
    return match && match.type === 'menu' ? match : null
  }, [config.items, state.openId])

  // We keep the last-rendered menu mounted across the close animation so
  // the grid track has actual content to shrink. NavBar's grid-rows
  // transition needs the cell content to remain its original height while
  // the track interpolates 1fr → 0fr; if we unmounted on the same frame
  // the close started, the track would snap to 0 instantly.
  const [keptMenu, setKeptMenu] = useState<TNavMenu | null>(null)
  useEffect(() => {
    if (currentMenu) setKeptMenu(currentMenu)
  }, [currentMenu])
  const handlePanelTransitionEnd = useCallback(
    (e: React.TransitionEvent<globalThis.HTMLElement>) => {
      if (e.propertyName !== 'grid-template-rows') return
      if (currentMenu === null) setKeptMenu(null)
    },
    [currentMenu]
  )

  // The pill's panel row hosts the mega menu on desktop and the whole
  // mobile menu below `md`, so one morph serves both.
  const mobileOpen = !isDesktop && state.mobileOpen
  const isPanelOpen = isDesktop ? currentMenu !== null : state.mobileOpen

  // A viewport that grows past `md` while the mobile menu is open would
  // leave the body scroll-locked with nothing showing.
  const { closeMobile } = state
  useEffect(() => {
    if (isDesktop && state.mobileOpen) closeMobile()
  }, [isDesktop, state.mobileOpen, closeMobile])

  // Lock page scroll behind the open mobile menu.
  useEffect(() => {
    if (!mobileOpen) return
    const body = globalThis.document?.body
    if (!body) return
    const prev = body.style.overflow
    body.style.overflow = 'hidden'
    return () => {
      body.style.overflow = prev
    }
  }, [mobileOpen])

  // ESC + outside-click for whichever surface is open — owned here because
  // the panel and bar share a single container ref.
  const closeAll = useCallback(() => {
    state.closeMenu()
    state.closeMobile()
  }, [state.closeMenu, state.closeMobile])
  useEffect(() => {
    if (!isPanelOpen) return
    function handleKey(e: globalThis.KeyboardEvent) {
      if (e.key === 'Escape') closeAll()
    }
    function handleClickOutside(e: globalThis.MouseEvent) {
      const nav = navRef.current
      if (!nav) return
      if (nav.contains(e.target as globalThis.Node)) return
      closeAll()
    }
    globalThis.document?.addEventListener('keydown', handleKey)
    globalThis.document?.addEventListener('mousedown', handleClickOutside)
    return () => {
      globalThis.document?.removeEventListener('keydown', handleKey)
      globalThis.document?.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isPanelOpen, closeAll])

  const panelContent = isDesktop ? (
    keptMenu ? (
      <DesktopMegaMenu menu={keptMenu} onLinkClick={state.closeMenu} />
    ) : null
  ) : (
    // Always mounted below `md` so the close animation has content to shrink.
    <MobileMenu
      open={state.mobileOpen}
      stack={state.mobileStack}
      config={config}
      onPush={state.pushMobile}
      onNavigate={state.closeMobile}
    />
  )

  const panelLabel = isDesktop
    ? ((isPanelOpen ? currentMenu?.label : keptMenu?.label) ?? null)
    : config.labels.mainMenu

  return (
    <>
      {!isDesktop && (
        <Box
          aria-hidden="true"
          onClick={state.closeMobile}
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: theme.zIndex.appBar - 1,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? 'rgba(0,0,0,0.5)'
                : 'rgba(0,0,0,0.22)',
            opacity: mobileOpen ? 1 : 0,
            pointerEvents: mobileOpen ? 'auto' : 'none',
            transition: 'opacity 360ms cubic-bezier(0.4, 0, 0.6, 1)',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          }}
        />
      )}
      <NavBar
        ref={navRef}
        config={config}
        openId={state.openId}
        mobileOpen={mobileOpen}
        mobileDepth={mobileOpen ? state.mobileStack.length : 0}
        isCoarsePointer={state.isCoarsePointer}
        isDesktop={isDesktop}
        onMenuTriggerEnter={(id) => {
          cancelCloseTimer()
          state.openMenu(id)
        }}
        onMenuTriggerFocus={state.openMenu}
        onMenuTriggerClick={(id) => {
          cancelCloseTimer()
          if (state.openId === id) state.closeMenu()
          else state.openMenu(id)
        }}
        onLinkHover={() => {
          if (state.openId) state.closeMenu()
        }}
        onHamburgerClick={state.toggleMobile}
        onBackClick={state.popMobile}
        onPanelPointerEnter={cancelCloseTimer}
        onPanelPointerLeave={scheduleClose}
        onPanelTransitionEnd={handlePanelTransitionEnd}
        panelContent={panelContent}
        panelLabel={panelLabel}
        isPanelOpen={isPanelOpen}
        barHeight={barHeight}
        viewportHeight={viewportHeight}
      />
    </>
  )
}

/**
 * Height of the area the user can actually see, in px, or `null` before
 * mount. The mobile card is sized from this instead of `100dvh`: on iOS
 * Safari the dynamic viewport units lag the toolbar as it collapses and
 * expands, which left the open card taller than the screen and pushed its
 * bar row out of view. `visualViewport` reports every toolbar change.
 */
function useVisualViewportHeight(): number | null {
  const [height, setHeight] = useState<number | null>(null)
  useEffect(() => {
    const win = globalThis.window
    if (!win) return
    const vv = win.visualViewport
    const read = () => setHeight(Math.round(vv?.height ?? win.innerHeight))
    read()
    vv?.addEventListener('resize', read)
    win.addEventListener('resize', read)
    win.addEventListener('orientationchange', read)
    return () => {
      vv?.removeEventListener('resize', read)
      win.removeEventListener('resize', read)
      win.removeEventListener('orientationchange', read)
    }
  }, [])
  return height
}
